// app/lib/tracking/types.ts

// ===== Consent =====
export type ConsentCategories = {
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
};

export const DEFAULT_CONSENT: ConsentCategories = {
  analytics: false,
  marketing: false,
  functional: false,
};

export const CONSENT_STORAGE_KEY = "cookie_consent";
export const CONSENT_VERSION = "2";

export type StoredConsent = {
  version: string;
  timestamp: number;
  categories: ConsentCategories;
};

// ===== Consent Backend API =====
export type ConsentApiResponse = {
  status: boolean;
  data: {
    categories: ConsentCategories;
    version: string;
    updated_at: string;
  } | null;
};

// ===== Ecommerce Types (GA4 standardına uygun) =====
export type EcommerceItem = {
  item_id: string | number;
  /**
   * Meta / TikTok KATALOG kimliği — Merchant Center feed'indeki `g:id`.
   *
   * `item_id` ile aynı ŞEY DEĞİL ve karıştırılmamalı:
   *   • item_id     → GA4'e gider, ÜRÜN seviyesi (product.id, ör. 92343)
   *   • content_id  → Meta/TikTok'a gider, BEDEN seviyesi variant_id (ör. 916187)
   *
   * Feed'den doğrulandı (api/sitemap/google-merchant): `g:id` her (renk, beden)
   * kombinasyonu için ayrı — 36.351 item'ın 36.351'i benzersiz. Renk seviyesini
   * `g:item_group_id` taşıyor. Örnek: aynı ayakkabının 40/41/44 bedenleri
   * 1000893 / 1000894 / 1000897 g:id'lerini alıyor, item_group_id'leri 1000893.
   *
   * Bizdeki karşılığı sepete eklerken kullandığımız variant_id ile AYNI:
   *   • Product (listeleme) → sizes[n].variant_id
   *   • ProductDetail       → sizes[n].id
   * (bkz. product.types.ts — iki tipte alan adı farklı, ID uzayı aynı)
   *
   * Doldurulmazsa events.ts item_id'ye düşer; katalog eşleşmesi ürün
   * seviyesinde kalır, yani reklam tarafı çalışır ama beden kırılımı kaybolur.
   */
  content_id?: string | number;
  item_name: string;
  item_category?: string;
  item_brand?: string; // Gerçek ürün markası (brand_name) — satıcı adı DEĞİL
  affiliation?: string; // Pazaryerinde: satıcı/mağaza adı
  price: number;
  quantity?: number;
  currency?: string;
  /** Birim başına indirim tutarı (list_price - price). GA4 standardı. */
  discount?: number;
  /** Ürünün listedeki sırası. Detay sayfasında tek ürün var → 0. GA4 standardı. */
  index?: number;
  /** Seçili varyant, tek string: "Kremsiyahlı / 38". GA4 standardı. */
  item_variant?: string;
  /** GA4 standart parametresi DEĞİL — item-scoped custom dimension olarak
   *  GA4 arayüzünde tanımlanmazsa raporlarda görünmez. */
  gender?: string;
};

export type PurchaseEventData = {
  transaction_id: string;
  value: number;
  currency?: string;
  tax?: number;
  shipping?: number;
  items: EcommerceItem[];
  user?: {
    email?: string;
    phone?: string;
    first_name?: string;
    last_name?: string;
    city?: string;
    country?: string;
  };
};

export type AddToCartEventData = {
  value: number;
  currency?: string;
  items: EcommerceItem[];
};

export type ViewItemEventData = {
  value: number;
  currency?: string;
  items: EcommerceItem[];
};

export type BeginCheckoutEventData = {
  value: number;
  currency?: string;
  items: EcommerceItem[];
};

export type ViewCartEventData = {
  value: number;
  currency?: string;
  items: EcommerceItem[];
};

export type RemoveFromCartEventData = {
  value: number;
  currency?: string;
  items: EcommerceItem[];
};

export type AddShippingInfoEventData = {
  value: number;
  currency?: string;
  items: EcommerceItem[];
};

export type AddPaymentInfoEventData = {
  value: number;
  currency?: string;
  payment_type?: string;
  items: EcommerceItem[];
};

// ===== Server-Side Event (Facebook CAPI vb.) =====
export type ServerEventPayload = {
  event_name: string; // "Purchase" | "AddToCart" | ...
  event_id: string; // Browser & server deduplication için
  event_source_url: string;
  data: Record<string, unknown>;
  user_data?: {
    email?: string;
    phone?: string;
    first_name?: string;
    last_name?: string;
    city?: string;
    country?: string;
  };
};

// ===== Window Augmentation =====
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    gtag: (...args: unknown[]) => void;
    fbq: (...args: unknown[]) => void;
    ttq: {
      track: (event: string, params?: Record<string, unknown>) => void;
      page: () => void;
      identify: (params: Record<string, unknown>) => void;
      [key: string]: unknown;
    };
    clarity: (...args: unknown[]) => void;
  }
}

export {};
